import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { SP } from '../../theme/tokens'

/**
 * useOverflowMask — the soft edge on a horizontally scrollable row.
 *
 * @description
 *   Watches one scroll container and returns an `sx` fragment that fades the
 *   edge(s) at which content really continues. A clipped row looks like a
 *   complete one: the sixth tab is simply gone and nothing tells the reader that
 *   something still sits to the right (measured at 390 px: 48 px of overflow in
 *   `UnderlineTabs`). While content lies at an edge the row carries a soft edge
 *   there; at the stop it disappears again, otherwise the hint would itself be
 *   the lie.
 *
 *   The mask is `mask-image`, not a colour gradient: it needs NO surface colour
 *   and is therefore correct on every ground the row stands on (paper, panel,
 *   page background). `black` and `transparent` in the gradient are the ALPHA
 *   channel, not a colour value (Regel 22 untouched); the soft edge WIDTH comes
 *   from the theme (`SP.md`, 24 px) so no measure is written as a literal.
 *
 *   The logic is the one `UnderlineTabs` carries inline (M1, 2026-10-09); this
 *   hook lifts it so tabs, Z2, Z3 and `TableViewport` share ONE implementation
 *   instead of four copies. `UnderlineTabs` itself is NOT changed in G0.1 — it
 *   adopts the hook in G0.7.
 *
 *   Re-measured on scroll, on container resize (`ResizeObserver`) and on mount.
 *   🔴 `ResizeObserver` watches the BOX of the container, not its content: if the
 *   content grows while the box stays (a longer label, another counter —
 *   `scrollWidth` is no box measure) it does NOT fire. A caller whose content
 *   changes without the box changing passes `deps` (or calls `remeasure`); in G0.7
 *   `UnderlineTabs` maps its `value` follow-up onto `deps` that way.
 *   `maskSx` is EMPTY while nothing overflows — the default is "no mask".
 *
 * @useWhen
 *   - A row that scrolls sideways inside its own container (tab rows, pill rows, a filter-chip row)
 *   - A table viewport that clips its columns and should say so
 *
 * @dontUseFor
 *   - A row that WRAPS instead of scrolling — there is no edge to fade
 *   - A page-level horizontal overflow — that is a layout defect, not a hint
 *
 * @related
 *   - UnderlineTabs — its inline copy of this logic; adopts the hook in G0.7
 *   - TableViewport — the table half that takes the mask as `edgeHint`
 *
 *   🔴 **`leftEdge: false` gibt es für den Fall, dass der LINKE Rand bereits
 *   verdeckt ist** (UI2 Teil 10, G0.8 / BS14): `TableViewport`s `stickyFirstColumn`
 *   heftet die erste Spalte auf `left: 0` — der Rand, den die Maske fade, liegt
 *   dann HINTER der klebenden Spalte, und der Verlauf schnitte die ersten 24 px
 *   einer Spalte ab, die vollständig sichtbar ist. Der Hinweis zeigte an der
 *   falschen Kante. Die rechte Maske bleibt unberührt; sie ist die einzige, die
 *   dort etwas verschweigen kann.
 *
 * @reuseHints
 *   - FilterPillRow's scroll row (BS8) once it stops wrapping
 *   - The Z3 active-filter row, which breaks "never wraps" into a "+n more" toggle
 *
 * @param {object} ref   React ref to the scroll container (the element that has `overflowX`)
 * @param {object} [options]
 * @param {number} [options.softEdge] Soft-edge width in theme spacing units (default `SP.md`)
 * @param {boolean} [options.leftEdge] `true` (default) renders the left edge while
 *   content lies there; `false` never does — for a container whose left side is
 *   already covered by a sticky column (`TableViewport.stickyFirstColumn`)
 * @param {Array} [options.deps] Re-measure when one of these changes — for content
 *   that grows inside an unchanged box (the `ResizeObserver` cannot see that).
 *   Keep the LENGTH of the array constant across renders; pass none if unused.
 * @returns {{ maskSx: object, remeasure: Function }} `{}` while nothing overflows,
 *   else `{ maskImage, WebkitMaskImage }`. `remeasure()` measures by hand.
 */
const KEINE = []

export default function useOverflowMask(ref, { softEdge = SP.md, deps = KEINE, leftEdge = true } = {}) {
  const theme = useTheme()
  const [kanten, setKanten] = useState({ left: false, right: false })

  const messen = useCallback(() => {
    const el = ref?.current
    if (!el) {
      // Kein Element: „keine Kante" ist die richtige Aussage, nicht „unbekannt".
      setKanten(prev => (prev.left || prev.right ? { left: false, right: false } : prev))
      return
    }
    // 1 px Toleranz: subpixel-genaue Scrollpositionen (Zoom, HiDPI) wuerden die
    // Kante sonst flackern lassen.
    // 🔴 `leftEdge: false` heisst: die linke Kante wird gar nicht erst gefuehrt.
    // Nicht „sie wird nicht gezeichnet" — sie ist keine Kante dieses Kastens,
    // weil die klebende Spalte davor liegt (`TableViewport.stickyFirstColumn`).
    const left = leftEdge && el.scrollLeft > 1
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    setKanten(prev => (prev.left === left && prev.right === right ? prev : { left, right }))
  }, [ref, leftEdge])

  useEffect(() => {
    messen()
    const el = ref?.current
    if (!el) return undefined
    el.addEventListener('scroll', messen, { passive: true })
    let beobachter
    if (typeof ResizeObserver !== 'undefined') {
      beobachter = new ResizeObserver(messen)
      beobachter.observe(el)
    }
    return () => {
      el.removeEventListener('scroll', messen)
      if (beobachter) beobachter.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `deps` ist der Nachlauf des Aufrufers (Inhaltsaenderung ohne Boxaenderung); `messen`/`ref` stehen davor und werden sehr wohl gelesen
  }, [messen, ref, ...deps])

  const maskSx = useMemo(() => {
    if (!kanten.left && !kanten.right) return {}
    const weich = theme.spacing(softEdge)
    const links = kanten.left ? `transparent 0, black ${weich}` : 'black 0'
    const rechts = kanten.right ? `black calc(100% - ${weich}), transparent 100%` : 'black 100%'
    const maske = `linear-gradient(to right, ${links}, ${rechts})`
    return { maskImage: maske, WebkitMaskImage: maske }
  }, [kanten.left, kanten.right, softEdge, theme])

  return { maskSx, remeasure: messen }
}
